<script>
  import { descending, index } from 'd3-array';
  import { derived } from 'svelte/store';
  import RenderedMap from './RenderedMap.svelte';
  import { getColor, NEUTRAL, TIE } from '../colors.js';
  import { formatPercent } from '../utils/format.js';
  import { partyClass } from '../utils/race-helpers.js';

  /**
   * @type {import('../types').MapLayer<GeoJSON.Polygon | GeoJSON.MultiPolygon>}
   */
  export let geography;

  /**
   * @type {import('../types').MapLayer}
   */
  // export let roads;

  /**
   * @type {import('../types').MapLayer<GeoJSON.Point>}
   */
  export let cities;

  export let includeParty = true;

  /**
   * A single race file (createSingleRaceFeed), the only feed with countyResults
   * @type {import('../types').ReadableRaceStore}
   */
  export let feed;

  // choices keyed by choiceId, counties keyed by fipsCode (the geography code)
  $: candidates = derived(feed, ($race) =>
    index($race.attributes.choices, (d) => d.choiceId)
  );
  $: subunits = derived(feed, ($race) =>
    index($race.attributes.countyResults ?? [], (d) => d.fipsCode)
  );

  /** @param {import('../types').CountyResult} county */
  const sortedChoices = (county) =>
    county.choices.slice().sort((a, b) => descending(a.votes, b.votes));

  $: getFillStyle = (/** @type {GeoJSON.Feature} */ feature) => {
    const code = /** @type {string} */ (feature.properties?.code);
    const subunit = $subunits.get(code);

    /** @type {string} */
    let color;

    if (!subunit) {
      color = NEUTRAL;
    } else if (subunit.totalVotes <= 0 || subunit.eevp < 25) {
      color = NEUTRAL;
    } else {
      const [leader, second] = sortedChoices(subunit);

      if (second && leader.votes === second.votes) {
        color = TIE;
      } else {
        const candidate = $candidates.get(leader.choiceId);
        color = getColor(candidate?.party?.abbr ?? 'other');
      }
    }

    return color;
  };

  $: findRaceData = (
    /** @type {GeoJSON.Feature | undefined} */ feature
  ) => {
    if (feature && feature.properties) {
      const subunit = $subunits.get(feature.properties.code);

      if (subunit) {
        const cands = sortedChoices(subunit)
          .filter(({ votes }) => votes > 0)
          .slice(0, 3)
          .map(({ choiceId, votes }) => {
            const candidate = $candidates.get(choiceId);
            return /** @type {import('../types.d').TooltipCandidateInterface} */ ({
              ap_id: choiceId,
              name: candidate?.last ?? candidate?.title ?? choiceId,
              party: candidate?.party?.abbr ?? '',
              votes,
              votePercent: formatPercent(votes, subunit.totalVotes),
            });
          });

        /** @type {import('../types.d').TooltipRaceInterface} */
        const tooltipRace = {
          name: subunit.county,
          candidates: cands,
          totalVotes: subunit.totalVotes,
          precinctsTotal: subunit.precinctsTotal,
          precinctsReporting: subunit.precinctsReporting,
          eevp: subunit.eevp,
          includeParty,
        };
        return tooltipRace;
      }
    }
    return undefined;
  };

  function generateFirstSentence() {
    return `Map of results in the ${$feed.attributes.title} race, by county.`;
  }

  /** @param {import('../types').Race} race */
  function getLeadingCount(race) {

    let noResults = false;
    let tie = false;
    /** @type {Record<string, number>} counties each candidate leads, by choiceId */
    let candidateIds = {}
    let tieCount = 0;
    let noResultsCount  = 0;

    for (const subunit of race.attributes.countyResults ?? []) {
      // first check if there are votes at all, or not enough in
      if (subunit.totalVotes <= 0 || subunit.eevp < 25) {
        noResults = true;
        noResultsCount++ 
        continue;
      }
      const [leader, second] = sortedChoices(subunit);
      const leaderApId = leader.choiceId;

      // next check if it's a tie
      if (second && leader.votes === second.votes) {
        tie = true;
        tieCount++
        continue;
      }

      if (!candidateIds[leaderApId]) {
        candidateIds[leaderApId] = 0;
      }

      candidateIds[leaderApId]++;
    }

    // Get candidates as a list
    let candidateList = Object.entries(candidateIds)

    // Sort the list
    let sortedCandidateList = candidateList.sort((cand1, cand2) => {
      return cand2[1] - cand1[1]
    })

    // build the json
    let countyCountData = {
      'candidates': sortedCandidateList,
      'tie': tieCount,
      'noLeader': noResultsCount
    }

    return countyCountData;
  }

  $: countyCountResults = getLeadingCount($feed);

  function generateTiedText(/** @type {number} */ x){
    if (x > 0) {
      return `There are ties between the candidates in ${x} ${x > 1 ? 'counties' : 'county'}.` 
    } else {
      return '';
    }
  }

  function generateNoLeaderText(/** @type {number} */ x){
    if (x > 0) {
      return `A leader has not yet been called in ${x} ${x > 1 ? 'counties' : 'county'}.` 
    } else {
      return '';
    }
  }

  /**
   * @param {ReturnType<typeof getLeadingCount>} resultsJson
   * @param {number | undefined} totalCounties
   */
  function generateLeaderText(resultsJson, totalCounties) {
    // find out how many candidates are appearing
    let candidateCount  = resultsJson.candidates.length;

    let leader1={};
    let leader2={};
    let leader3={};

    if ((candidateCount !== 0) && (resultsJson.candidates[0])){
      leader1.name = $candidates.get(resultsJson.candidates[0][0])?.last;
      leader1.count = resultsJson.candidates[0][1];
    }
    if ((candidateCount !== 0) && (resultsJson.candidates[1])){
      leader2.name = $candidates.get(resultsJson.candidates[1][0])?.last;
      leader2.count = resultsJson.candidates[1][1];
    }
    // if third candidate
    if ((candidateCount !== 0) && (resultsJson.candidates[2])){
      leader3.name = $candidates.get(resultsJson.candidates[2][0])?.last;
      leader3.count = resultsJson.candidates[2][1];
    }

    if (resultsJson.noLeader == totalCounties) { // no results at all on map
      return `A leader has not yet been called in any county.`
    } else if (candidateCount === 1){ // if only one leader atm
      return `${leader1.name} is leading in ${leader1.count} counties.`
    } else if (candidateCount === 2){ // if two leaders atm
      return `${leader1.name} is leading in ${leader1.count} counties. ${leader2.name} is leading in ${leader2.count} counties.`
    } else if (candidateCount === 3){ // if three leaders atm
      return `${leader1.name} is leading in ${leader1.count} counties. ${leader2.name} is leading in ${leader2.count} counties. ${leader3.name} is leading in ${leader3.count} counties.`
    } else if ((candidateCount === 2) && (leader1.count == leader2.count) && (leader1.count > 1)) {
      return `${leader1.name} and ${leader2.name} are both leading in ${leader2.count} counties each.`
    } else {
      return ''
    }
    
  }

  $: ariaLabel = `${generateFirstSentence()} ${generateLeaderText(countyCountResults, $feed.attributes.countyResults?.length)} ${generateTiedText(countyCountResults.tie)} ${generateNoLeaderText(countyCountResults.noLeader)}`
</script>

<RenderedMap {getFillStyle} {findRaceData} {geography} {cities} {ariaLabel}/>
